import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ArrowLeft, CreditCard, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import copy from '../copy';

const Cart: React.FC = () => {
  const { state, removeItem, clearCart } = useCart();

  if (state.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{copy.carrito_vacio}</h1>
          <p className="text-gray-600 mb-8">{copy.carrito_vacio_desc}</p>
          <Link to="/cursos" className="btn-primary">
            {copy.carrito_explorar}
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/" className="text-gray-600 hover:text-primary-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{copy.carrito_titulo}</h1>
          </div>
          <button
            onClick={clearCart}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            {copy.carrito_vaciar}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {state.items.map((item) => (
              <div key={item.course.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex space-x-4">
                  <img
                    src={item.course.image}
                    alt={item.course.title}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.course.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-2">
                      {item.course.shortDescription}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => removeItem(item.course.id)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary-600">
                          {formatPrice(item.course.price)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">{copy.checkout_resumen}</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{copy.carrito_subtotal}:</span>
                    <span className="font-medium">{formatPrice(state.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{copy.carrito_impuestos}:</span>
                    <span className="font-medium">{formatPrice(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{copy.carrito_envio}:</span>
                    <span className="font-medium text-green-600">{copy.carrito_gratis}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-bold text-gray-900">{copy.carrito_total}:</span>
                      <span className="text-lg font-bold text-primary-600">
                        {formatPrice(state.total)}
                      </span>
                    </div>
                  </div>
                </div>

                <Link to="/checkout" className="btn-primary w-full mb-4 inline-flex items-center justify-center">
                  <CreditCard className="mr-2 w-5 h-5" />
                  {copy.carrito_proceder_pago}
                </Link>

                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <Lock className="w-4 h-4" />
                  <span>{copy.carrito_pago_seguro}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 